import { BtixConstant } from "../constant";
import { disableEventElement, enableEventElement } from "./disable";
import { showLoading } from "./loading";
import { hideLoading } from "./loading";
import { validateForm } from "./validation";
/**
 * 雑な使い方説明
 *
 * ## 最低限設定すべき内容
 *
 * - 確認画面の移動対象領域全体:
 *   - `.form-area-can-preview` を付与
 *     - このエリア内にしか影響範囲を出さないための処置
 *     - 1ページにフォームが複数あるパターンへの配慮（モーダル含む）
 * - 確認画面に移動させるボタン:
 *   - `.button-form-confirm` を付与
 *     - 複数のボタンを設置してOKです
 * - 編集画面に戻るボタン:
 *   - `.button-form-back-to-edit` を付与
 *
 * ## 要素の表示状態をコントロールする方法
 *
 * - 編集画面でのみ表示したい要素:
 *   - `.area-only-show-edit` を付与
 * - 確認画面でのみ表示したい要素:
 *   - `.area-only-show-confirm` と `.area-hide` を付与
 *   - 複数のボタンを設置した場合、Aパターンでは表示したいが、Bパターンでは表示したくない、みたいなのが出てきます
 *     - 上記の場合は 確認画面に移動させる/編集画面に戻るボタンに以下のようなdata属性を付けてください
 *       - `data-confirm-show-class="area-only-show-confirm-XXXXXX"`
 *     - 上記の場合、JavaScript側はdata属性に従い `.area-only-show-confirm-XXXXXX` を参照します
 *
 * ## オプションの設定項目
 *
 * - 確認画面で表示する内容を装飾したい場合
 *   - select, input[type="checkbox"] の親要素(`.form-select` もしくは `.form-checkbox`)に `data-separate-type="keyword"` を設定することができます
 *     - 設定できるキーワードは以下の通りです
 *       - `br`: "<br>" で join されます
 *       - `space`: " " で join されます
 *       - `comma`: "," で join されます
 *       - `list`: "<ul><li>...<li><ul>" で join されます
 *
 * - 確認画面で表示する内容の前後に文字を追加したい場合（第n番、n枚、など）
 *   - input, select, textarea 要素に以下を設定することができます
 *     - `data-prefix-text="prefixKeyword"`: prefixKeyword が文字列の前に付与されます
 *     - `data-suffix-text="suffixKeyword"`: suffixKeyword が文字列の後に付与されます
 *   - 例: `<input type="text" value="5" data-suffix-text="枚">` の場合
 *     - レスポンスは `5枚` となります
 */
// MEMO: これはクエリセレクタで呼ばないので.をつけない
const displayNoneClassName = "area-hide";
const confirmModeClassName = "confirm-mode";
const formFieldSelector = ".form-area dd";
const formbackToEditButtonSelector = ".button-form-back-to-edit";
const onlyShowEditViewSelector = ".area-only-show-edit";
const formSubmitButtonSelector = ".button-form-confirm";
/**
 * 初期化処理
 */
export const initConfirmPage = () => {
    const canPreviewFormAreaListSelector = ".form-area-can-preview";
    const canPreviewFormAreaList = document.querySelectorAll(canPreviewFormAreaListSelector);
    if (!canPreviewFormAreaList)
        throw new Error("'canPreviewFormAreaList' is not found.");
    // フォームエリアの初期化
    canPreviewFormAreaList.forEach((canPreviewFormArea) => {
        setupPreviewFormButtons(canPreviewFormArea);
    });
};
const getConfirmButtons = (containerArea) => containerArea.querySelectorAll(formSubmitButtonSelector);
const getFormFieldList = (containerArea) => containerArea.querySelectorAll(formFieldSelector);
const getFormBackToEditButtons = (containerArea) => containerArea.querySelectorAll(formbackToEditButtonSelector);
const getOnlyShowEditViewList = (containerArea) => containerArea.querySelectorAll(onlyShowEditViewSelector);
/**
 * 個別のプレビューボタンを初期化
 *
 * formSubmitButton は1ページに1つ以上ある。
 * 確認ページが「保存する」「下書きする」などのパターンがあるため。
 *
 * @param canPreviewFormArea このElement以下がフォームエリアになる（スコープはここを起点とする）
 */
const setupPreviewFormButtons = (canPreviewFormArea) => {
    const formConfirmButtons = getConfirmButtons(canPreviewFormArea);
    // フォーム確認画面への遷移が不要なページでは何もしない
    if (formConfirmButtons.length < 1)
        return;
    if (BtixConstant.debugMode)
        window.console.log("init form with confirm:", canPreviewFormArea);
    const formBackToEditButtons = getFormBackToEditButtons(canPreviewFormArea);
    setupFormConfirmButtons(formConfirmButtons, canPreviewFormArea);
    setupBackToEditButtons(formBackToEditButtons, canPreviewFormArea);
};
const setupFormConfirmButtons = (formConfirmButtons, canPreviewFormArea) => {
    formConfirmButtons.forEach((formConfirmButton) => {
        // 確認ボタンを押した時の処理
        formConfirmButton.addEventListener(BtixConstant.touchOrClickEvent(), (e) => {
            const event = e;
            const target = event.target;
            const formField = target.closest("form");
            if (!formField)
                throw new Error("'formField' is not found.");
            // 処理中に何度も実行されないようボタンをdisabledにする
            disableEventElement(event);
            const formFieldList = getFormFieldList(canPreviewFormArea);
            const onlyShowEditViewList = getOnlyShowEditViewList(canPreviewFormArea);
            const onlyShowConfirmViewList = getOnlyShowConfirmViewListFromConfirmShowClass(formConfirmButton, canPreviewFormArea);
            const onlyShowEditOutSideViewList = document.querySelectorAll("[data-show-only-edit-outside]");
            // 処理中モーダル表示
            showLoading();
            validateForm(target, formField)
                .then((result) => {
                if (!result) {
                    if (BtixConstant.debugMode)
                        window.console.error("validation error occured.");
                    return;
                }
                if (BtixConstant.debugMode)
                    window.console.log("click:", formConfirmButton);
                formFieldList.forEach((formField) => {
                    setupConfirmFields(formField);
                });
                showConfirmMessageAndHideEditMessage(onlyShowEditViewList, onlyShowConfirmViewList);
                hideOnlyShowEditOutSideViewList(onlyShowEditOutSideViewList);
            })
                .catch((result) => {
                if (BtixConstant.debugMode)
                    window.console.error(result);
            })
                .finally(() => {
                // disabledにしたボタンをもとに戻す
                enableEventElement(event);
                // 処理中モーダルを消す
                hideLoading();
            });
            event.preventDefault();
            // formタグの位置までスクロールする
            const formRect = formField.getBoundingClientRect();
            if (formField.classList.contains("scroll-top")) {
                window.scrollTo(formRect.left + window.scrollX, 0);
            }
            else {
                window.scrollTo(formRect.left + window.scrollX, formRect.top + window.scrollY);
            }
        });
    });
};
// 編集画面に戻るボタン押した時の処理
const setupBackToEditButtons = (formBackToEditButtons, canPreviewFormArea) => {
    formBackToEditButtons.forEach((formBackToEditButton) => {
        formBackToEditButton.addEventListener(BtixConstant.touchOrClickEvent(), (e) => {
            const event = e;
            disableEventElement(event);
            const formFieldList = getFormFieldList(canPreviewFormArea);
            const onlyShowEditViewList = getOnlyShowEditViewList(canPreviewFormArea);
            const onlyShowConfirmViewList = getOnlyShowConfirmViewListFromConfirmShowClass(formBackToEditButton, canPreviewFormArea);
            const onlyShowEditOutSideViewList = document.querySelectorAll("[data-show-only-edit-outside]");
            if (BtixConstant.debugMode)
                window.console.log("click:", formBackToEditButton);
            formFieldList.forEach((formField) => {
                removeConfirmTextAndShowFormField(formField);
            });
            showEditMessageAndHideConfirmMessage(onlyShowEditViewList, onlyShowConfirmViewList);
            enableEventElement(event);
            displayOnlyShowEditOutSideViewList(onlyShowEditOutSideViewList);
            event.preventDefault();
        });
    });
};
// ========================================
// 表示内容を変更させる
// ========================================
// 編集画面から確認画面の表示内容に変える
const showConfirmMessageAndHideEditMessage = (onlyShowEditViewList, onlyShowConfirmViewList) => {
    onlyShowEditViewList.forEach((element) => {
        element.classList.add(displayNoneClassName);
    });
    onlyShowConfirmViewList.forEach((element) => {
        element.classList.remove(displayNoneClassName);
    });
};
// 確認画面から編集画面の表示内容に変える
const showEditMessageAndHideConfirmMessage = (onlyShowEditViewList, onlyShowConfirmViewList) => {
    onlyShowEditViewList.forEach((element) => {
        element.classList.remove(displayNoneClassName);
    });
    onlyShowConfirmViewList.forEach((element) => {
        element.classList.add(displayNoneClassName);
    });
};
// data-confirm-show-class に値が設定されていれば、そのclassを利用して表示の切り替えを行う
const getOnlyShowConfirmViewListFromConfirmShowClass = (formConfirmButton, canPreviewFormArea) => {
    const onlyShowConfirmViewSelector = formConfirmButton.dataset.confirmShowClass ? `.${formConfirmButton.dataset.confirmShowClass}` : ".area-only-show-confirm";
    const onlyShowConfirmViewList = canPreviewFormArea.querySelectorAll(onlyShowConfirmViewSelector);
    if (!onlyShowConfirmViewList)
        throw new Error("'onlyShowConfirmViewList' is not found.");
    return onlyShowConfirmViewList;
};
// 確認画面から編集画面に戻す
const removeConfirmTextAndShowFormField = (formField) => {
    // 確認文字列を削除する
    const confirmText = formField.querySelector("p.confirm-text");
    if (!confirmText)
        throw new Error("'confirmText' is not found.");
    confirmText.remove();
    // フォーム要素を表示する
    formField.querySelectorAll(`.${confirmModeClassName}`).forEach((formElement) => {
        formElement.classList.remove(confirmModeClassName, displayNoneClassName);
    });
};
// フォームの中身を確認用テキストに差し替える
const setupConfirmFields = (formField) => {
    const formElementSelector = "input, select, textarea";
    const formElements = formField.querySelectorAll(formElementSelector);
    if (!formElements)
        throw new Error("'formElements' is not found.");
    // MEMO: ここでpタグつくってるけど、別に最初から用意しても良い感じではある
    // MEMO: ここで document を親にしているが、ここは問題ないです
    const confirmTextElement = document.createElement("p");
    // フォーム要素を非表示にする
    formElements.forEach((formElement) => {
        formElement.classList.add(confirmModeClassName, displayNoneClassName);
    });
    // 確認用メッセージの表示を行う
    confirmTextElement.innerHTML = generateConfirmText(formField, formElements) ?? "";
    confirmTextElement.classList.add("confirm-text", "largeNarrow");
    formField.prepend(confirmTextElement);
};
// 確認用のメッセージを要素単位で生成する
// MEMO: ここの中で document.createElement() をしていないのは、戻り値を必ずstringにしておきたいため
const generateConfirmText = (formField, formElements) => {
    // メッセージリストを生成（ラジオボタンやチェックボックスなどの多要素パターンがあるため）
    const messages = [...formElements].map((formElement) => {
        // prefix/suffix の値を取得
        const prefixText = formElement.dataset.prefixText || formField.dataset.prefixText || "";
        const suffixText = formElement.dataset.suffixText || formField.dataset.prefixText || "";
        return getFormValue(formElement, prefixText, suffixText);
    });
    if (BtixConstant.debugMode)
        window.console.log("messages:", messages);
    // 指定された形式でメッセージを成形する
    const separateType = formField.dataset.separateType;
    switch (separateType) {
        case "br": {
            return messages.filter((message) => message !== null).join("<br>");
        }
        case "space": {
            return messages.filter((message) => message !== null).join(" ");
        }
        case "slash": {
            return messages.filter((message) => message !== null).join("/");
        }
        case "comma": {
            return messages.filter((message) => message !== null).join(", ");
        }
        case "list": {
            const resultMessage = messages
                .filter((message) => message !== null)
                .map((message) => {
                return `<li>${message}</li>`;
            })
                .join("");
            return `<ul>${resultMessage}</ul>`;
        }
        case "pair-list": {
            const pairs = [];
            for (const [index, message] of messages.filter((entry) => entry !== null).entries()) {
                const pairIndex = Math.floor(index / 2);
                const pair = pairs.at(pairIndex) ?? [];
                if (message !== null) {
                    pair.push(message);
                }
                pairs[pairIndex] = pair;
            }
            global.console.log(pairs);
            const resultMessage = pairs
                .filter((pair) => pair.length > 1)
                .map((pair) => {
                return `<li>${pair[0]}${pair[1]}</li>`;
            })
                .join("");
            return `<ul>${resultMessage}</ul>`;
        }
        default: {
            return messages.filter((message) => message !== null).join("");
        }
    }
};
/**
 * 各フォームの要素からテキストを抽出してやる
 *
 * @param formElement 各フォーム要素
 * @param prefixText テキスト抽出時に prefix として出力したい文字列
 * @param suffixText テキスト抽出時に suffix として出力したい文字列
 * @returns prefix/suffix付きの string もしくは null
 */
const getFormValue = (formElement, prefixText, suffixText) => {
    if (formElement instanceof HTMLInputElement) {
        let checkedFormTypes = [];
        // hiddenやsubmitは何も処理しない
        checkedFormTypes = ["hidden", "submit"];
        if (checkedFormTypes.includes(formElement.type)) {
            return null;
        }
        // ラジオボタン、チェックボックスの時はcheckedの値のみを返却
        checkedFormTypes = ["radio", "checkbox"];
        if (checkedFormTypes.includes(formElement.type)) {
            // チェックしていないものの値は返さない
            if (!formElement.checked)
                return null;
            // 普通にvalueを取得すると数字やキーワードになるため、label内のテキストを返却
            // label内にテキストが含まれていないときは「選択済」とレスポンスする
            const formParentArea = formElement.parentElement;
            if (!formParentArea)
                throw new Error("'formParentArea' is not found.");
            const checkedText = formParentArea.querySelector("label");
            if (!checkedText)
                throw new Error("'checkedText' is not found.");
            if (checkedText.innerText.length == 0)
                return "選択済";
            return prefixText + checkedText.innerText + suffixText;
        }
        // パスワードは表示しない
        checkedFormTypes = ["password"];
        if (checkedFormTypes.includes(formElement.type))
            return prefixText + "********" + suffixText;
        // ファイルの時はファイル名だけ表示
        checkedFormTypes = ["file"];
        if (checkedFormTypes.includes(formElement.type)) {
            const file = formElement.files?.item(0) ?? null;
            const fileUploadAreaSelector = ".file-upload-area";
            const uploadedFileNameSelector = ".file-uploaded-info span";
            const fileUploadArea = formElement.closest(fileUploadAreaSelector);
            if (!fileUploadArea)
                throw new Error("'fileUploadArea' is not found.");
            const uploadedFileNameArea = fileUploadArea.querySelector(uploadedFileNameSelector);
            if (!uploadedFileNameArea)
                throw new Error("'uploadedFileName' is not found.");
            // アップロード済みのファイルがあればその名前を取得する
            const uploadedFileName = uploadedFileNameArea.innerText;
            // ファイルが選択されていなく、かつアップロード済みのファイルもない場合は何も返却しない
            // MEMO: アップロード済みのファイルを自分自身で削除した場合もこの処理となる
            if (!file && !uploadedFileName)
                return null;
            // ファイルが選択されている場合はそのファイル名、そうでない場合はアップロード済みのファイル名を返却する
            const fileName = file ? file.name : uploadedFileName;
            return prefixText + fileName + suffixText;
        }
        if (formElement.value === "")
            return null; // 中身が空文字なら何も返却しない
        // 上記以外は普通にvalueを返却すればOK
        return prefixText + formElement.value + suffixText;
    }
    // セレクトボックスは中身を表示する
    if (formElement instanceof HTMLSelectElement) {
        if (formElement.disabled)
            return null; // セレクトボックスがdisabled状態ならそれは表示させない
        const selectedIndex = formElement.selectedIndex;
        return prefixText + formElement.options[selectedIndex].text + suffixText;
    }
    // テキストエリアはvalueを表示する
    if (formElement instanceof HTMLTextAreaElement) {
        if (formElement.value === "")
            return null; // 中身が空文字なら何も返却しない
        return prefixText + formElement.value + suffixText;
    }
    return null;
};
// canPreviewFormAreaの外の要素にarea-hideを付ける
function hideOnlyShowEditOutSideViewList(Elements) {
    for (const Element of Elements) {
        Element.classList.add(displayNoneClassName);
    }
}
// canPreviewFormAreaの外の要素からarea-hideを取り除く
function displayOnlyShowEditOutSideViewList(Elements) {
    for (const Element of Elements) {
        Element.classList.remove(displayNoneClassName);
    }
}
